import type { CSSProperties, ReactNode } from 'react';
import { LoadError } from './LoadError';

/**
 * Единый путь состояния загрузки блока (agroai-6al.49, журнал №521):
 *
 *   loading → скелетон в размер блока;
 *   error   → `LoadError` с «Повторить» (№293);
 *   empty   → `EmptyState` с причиной — ТОЛЬКО после завершённой загрузки;
 *   ready   → данные.
 *
 * Фаза считается одной функцией `loadPhase` по результату `useRequest`, поэтому
 * у страниц нет своей второй логики «а не пусто ли, пока грузится». Прежний
 * результат при фоновом обновлении (`refresh`) или ошибке повтора остаётся на
 * экране — блок не мигает скелетоном при смене среза.
 */

export type LoadPhase = 'loading' | 'error' | 'empty' | 'ready';

/** То, что возвращает `useRequest` (или собранное `allLoaded`). */
export interface Loadable<T> {
  state: string;
  data: T | null | undefined;
  error?: { message?: string } | null;
  reload?: () => void;
}

/**
 * Фаза блока. Данные есть — `ready` (или `empty` по `isEmpty`), даже если идёт
 * обновление или повтор упал: прежний результат не прячется. Данных нет —
 * `error`, если запрос упал; `empty`, если сервер ответил пустотой; иначе
 * `loading` (включая первый рендер до старта запроса).
 */
export function loadPhase<T>(req: Loadable<T>, isEmpty?: (data: T) => boolean): LoadPhase {
  if (req.data === null || req.data === undefined) {
    if (req.state === 'error') return 'error';
    if (req.state === 'ready') return 'empty';
    return 'loading';
  }
  return isEmpty?.(req.data) ? 'empty' : 'ready';
}

/** Блок ещё не может ничего сказать о данных: скелетон, а не «нет». */
export function isLoading(req: Loadable<unknown>): boolean {
  return loadPhase(req) === 'loading';
}

type DataOf<R> = R extends Loadable<infer T> ? T : never;

/**
 * Несколько запросов как один: ошибка любого без данных — ошибка; любой ещё
 * грузится — загрузка; иначе кортеж данных. `reload` повторяет все.
 */
export function allLoaded<const R extends readonly Loadable<unknown>[]>(
  ...reqs: R
): Loadable<{ [K in keyof R]: NonNullable<DataOf<R[K]>> }> {
  const failedReq = reqs.find((r) => loadPhase(r) === 'error');
  const anyLoading = reqs.some((r) => loadPhase(r) === 'loading');
  const allData = reqs.every((r) => r.data !== null && r.data !== undefined);
  return {
    state: failedReq ? 'error' : anyLoading ? 'loading' : 'ready',
    data: allData && !failedReq ? (reqs.map((r) => r.data) as never) : null,
    error: failedReq?.error ?? null,
    reload: () => reqs.forEach((r) => r.reload?.()),
  };
}

export interface AsyncBlockProps<T> {
  req: Loadable<T>;
  /** Что загружается, в винительном падеже: «темп кампании». */
  what: string;
  /** Скелетон в размер готового блока. */
  skeleton: ReactNode;
  /** Пусто ли пришедшее (по умолчанию пусто только `null`). */
  isEmpty?: (data: T) => boolean;
  /** Что показать при `empty` (обычно `EmptyState` с причиной). */
  empty?: ReactNode;
  /** Обёртка вокруг ошибки (карточка с заголовком), чтобы блок не терял рамку. */
  frame?: (body: ReactNode) => ReactNode;
  compact?: boolean;
  /** Зарезервированная высота: блок не схлопывается между фазами. */
  minHeight?: number | string;
  className?: string;
  children: (data: T) => ReactNode;
}

/** Блок с четырьмя состояниями. Подробности — у `loadPhase`. */
export function AsyncBlock<T>({ req, what, skeleton, isEmpty, empty, frame, compact = true, minHeight, className, children }: AsyncBlockProps<T>) {
  const phase = loadPhase(req, isEmpty);
  let body: ReactNode;
  if (phase === 'loading') body = skeleton;
  else if (phase === 'error') {
    const err = <LoadError compact={compact} what={what} error={req.error} onRetry={req.reload} />;
    body = frame ? frame(err) : err;
  } else if (phase === 'empty') body = frame && empty ? frame(empty) : (empty ?? null);
  else body = children(req.data as T);
  if (minHeight === undefined && !className) return <>{body}</>;
  const style: CSSProperties = { minHeight: typeof minHeight === 'number' ? `${minHeight}px` : minHeight };
  return <div className={className} style={style} data-phase={phase}>{body}</div>;
}
